import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Mic, Keyboard, Shuffle, ArrowRight, Loader2, PenLine, Lightbulb, Quote, BookOpen, Heart, Users, Compass, Gift } from "lucide-react";
import { GiftBanner } from "../components/GiftBanner";
import { JourneyIndicator } from "../components/JourneyIndicator";
import { ContinueMyStory } from "../components/ContinueMyStory";
import { ChapterNudges } from "../components/ChapterNudges";

function Stat({ value, label, testid }) {
  return (
    <div className="text-center" data-testid={testid}>
      <div className="font-serif text-2xl sm:text-3xl text-teal">{value}</div>
      <div className="text-xs text-subtle mt-0.5">{label}</div>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [question, setQuestion] = useState(null);
  const [progress, setProgress] = useState(null);
  const [tip, setTip] = useState(null);
  const [showcase, setShowcase] = useState(null);
  const [loading, setLoading] = useState(true);
  const name = user?.profile?.preferred_name;
  const pendingGift = typeof window !== "undefined" && !!localStorage.getItem("pending_gift_code");

  useEffect(() => {
    // One-time, idempotent: quietly organise any older/unclassified memories in the background.
    api.post("/memories/backfill-organise").catch(() => {});
    (async () => {
      try {
        const [q, p, t, s] = await Promise.all([
          api.get("/questions/today"),
          api.get("/progress"),
          api.get("/tips/daily").catch(() => ({ data: null })),
          api.get("/showcase").catch(() => ({ data: null })),
        ]);
        setQuestion(q.data);
        setProgress(p.data);
        setTip(t.data);
        setShowcase(s.data);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const another = async () => {
    const { data } = await api.get("/questions/next", { params: { exclude: question?.id || "" } });
    setQuestion(data);
  };

  const goQuiz = (mode) => navigate("/answer", { state: { question, mode } });
  const addMemory = (mode) => navigate("/answer", { state: { freeform: true, mode } });

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-teal" size={32} /></div>;
  }

  const hasMemories = progress?.memories > 0;

  return (
    <div className="space-y-8 animate-fade-in">
      <GiftBanner />
      {pendingGift && (
        <button data-testid="pending-gift-banner" onClick={() => navigate("/redeem")}
          className="w-full text-left rounded-2xl bg-coral/10 border border-coral/20 p-4 flex items-center gap-3 hover:bg-coral/15 transition-colors">
          <span className="w-10 h-10 rounded-xl bg-coral/15 text-coral flex items-center justify-center shrink-0"><Gift size={20} /></span>
          <span className="flex-1 text-sm text-charcoal"><b>You have a gift to claim.</b> Tap here to enter your code and unlock your year.</span>
          <ArrowRight size={16} className="text-coral" />
        </button>
      )}
      {/* Hero */}
      <div className="relative rounded-3xl overflow-hidden shadow-sm animate-fade-up">
        <img src="/memoirs-on-table.jpg" alt="Three finished memoirs resting on a table" className="w-full h-52 sm:h-72 object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-charcoal/20 to-transparent" />
        <div className="absolute bottom-0 left-0 p-6 sm:p-8">
          <p className="text-cream/80 text-xs uppercase tracking-widest font-semibold">Write My Story</p>
          <p className="text-cream font-serif text-2xl sm:text-3xl leading-snug mt-1 max-w-md">You lived the story. Now tell it.</p>
        </div>
      </div>

      <div>
        <p className="text-sm uppercase tracking-widest text-sage font-semibold">
          {hasMemories ? "Welcome back" : "Let's begin"}{name ? `, ${name}` : ""}
        </p>
        <h1 className="font-serif text-3xl sm:text-4xl text-charcoal mt-1">
          {hasMemories ? "Here's where you left off." : "Here's a question to get you talking today."}
        </h1>
        {hasMemories && (
          <p data-testid="home-accomplishment" className="text-subtle mt-2">
            You've saved {progress.memories} {progress.memories === 1 ? "memory" : "memories"}
            {progress.chapters > 0 && <> and created {progress.chapters} {progress.chapters === 1 ? "chapter draft" : "chapter drafts"}</>}.
          </p>
        )}
      </div>

      {/* Continue my story — the guided next step */}
      <ContinueMyStory />

      {/* Quiet prompts when a life stage is ready to become a chapter */}
      <ChapterNudges />

      {/* Today's question (the daily prompt) + Add a memory */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div data-testid="home-todays-question-card" className="bg-teal text-cream rounded-3xl p-7 flex flex-col animate-fade-up relative overflow-hidden">
          <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-sage/20 blur-2xl" />
          <p className="text-xs uppercase tracking-widest text-cream/60 font-semibold relative">Your question for today</p>
          <h2 className="font-serif text-2xl leading-snug mt-2 relative flex-1" data-testid="todays-question-text">{question?.text}</h2>
          <div className="mt-5 flex gap-2.5 relative">
            <button data-testid="speak-answer-btn" onClick={() => goQuiz("speak")}
              className="flex items-center justify-center gap-2 bg-coral hover:bg-coraldark text-white px-5 h-12 rounded-full font-semibold transition-colors">
              <Mic size={18} /> Speak
            </button>
            <button data-testid="type-answer-btn" onClick={() => goQuiz("type")}
              className="flex items-center justify-center gap-2 bg-cream/10 hover:bg-cream/20 border border-cream/30 text-cream px-5 h-12 rounded-full font-semibold transition-colors">
              <Keyboard size={18} /> Type
            </button>
          </div>
          <button data-testid="another-question-btn" onClick={another}
            className="mt-4 flex items-center gap-2 text-cream/80 hover:text-cream text-sm transition-colors relative">
            <Shuffle size={16} /> Ask me something else
          </button>
          <button data-testid="browse-questions-btn" onClick={() => navigate("/remember")}
            className="mt-2 flex items-center gap-2 text-cream/70 hover:text-cream text-sm transition-colors relative">
            <Compass size={15} /> Browse questions by theme
          </button>
        </div>

        <div data-testid="home-add-memory-card" className="bg-white rounded-3xl border border-line p-7 flex flex-col animate-fade-up">
          <span className="w-12 h-12 rounded-2xl bg-coral/10 text-coral flex items-center justify-center"><PenLine size={24} /></span>
          <h2 className="font-serif text-2xl text-charcoal mt-4">Add your own memory</h2>
          <p className="text-subtle mt-1.5 flex-1">Something already on your mind? Just tell us in your own words — no question needed. We'll help you find the story in it.</p>
          <div className="mt-5 flex gap-2.5">
            <button data-testid="home-add-speak-btn" onClick={() => addMemory("speak")}
              className="flex items-center justify-center gap-2 bg-cream2 hover:bg-sagelight text-charcoal px-5 h-12 rounded-full font-semibold transition-colors">
              <Mic size={18} /> Speak
            </button>
            <button data-testid="home-add-type-btn" onClick={() => addMemory("type")}
              className="flex items-center justify-center gap-2 bg-cream2 hover:bg-sagelight text-charcoal px-5 h-12 rounded-full font-semibold transition-colors">
              <Keyboard size={18} /> Type
            </button>
          </div>
        </div>
      </div>

      {/* Tip of the day */}
      {tip && (
        <div data-testid="tip-of-day" className="bg-cream2 rounded-2xl border border-line p-5 flex items-start gap-4 animate-fade-up">
          <span className="w-10 h-10 rounded-xl bg-coral/10 text-coral flex items-center justify-center shrink-0"><Lightbulb size={20} /></span>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-widest text-sage font-semibold">Juliet's Tip · {tip.category}</p>
            <p className="font-serif text-lg text-charcoal mt-1">{tip.title}</p>
            <p className="text-subtle text-sm mt-1 leading-relaxed">{tip.body}</p>
            <button data-testid="more-tips-btn" onClick={() => navigate("/tips")} className="mt-2 text-teal text-sm font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all">
              More writing tips <ArrowRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Memoir journey */}
      {progress && <JourneyIndicator progress={progress} chapters={progress.chapters_forming} />}

      {/* Progress */}
      {hasMemories && (
        <div className="bg-white rounded-3xl border border-line p-6 sm:p-8 animate-fade-up">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-serif text-xl text-charcoal">Your book so far</h3>
            <button data-testid="view-story-btn" onClick={() => navigate("/story")} className="text-teal text-sm font-semibold flex items-center gap-1 hover:gap-2 transition-all">
              My Book <ArrowRight size={16} />
            </button>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-4">
            <Stat testid="stat-memories" value={progress.memories} label="memories" />
            <Stat testid="stat-stories" value={progress.stories} label="stories" />
            <Stat testid="stat-people" value={progress.people} label="people" />
            <Stat testid="stat-photos" value={progress.photos} label="photos" />
            <Stat testid="stat-chapters" value={progress.chapters} label={progress.chapters === 1 ? "chapter draft" : "chapter drafts"} />
            <Stat testid="stat-words" value={(progress.words_written || 0).toLocaleString()} label="words written" />
          </div>
        </div>
      )}

      {!hasMemories && (
        <p className="text-center text-subtle text-sm px-6">
          There's no right place to start. Pick either option above and just talk, as if you were telling
          a friend. We'll take care of the rest.
        </p>
      )}

      {/* Invite family */}
      <div data-testid="home-invite-family" className="rounded-3xl bg-coral text-white p-7 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5 animate-fade-up shadow-sm">
        <span className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center shrink-0"><Heart size={28} /></span>
        <div className="flex-1">
          <h3 className="font-serif text-2xl">Ask your family to help</h3>
          <p className="text-white/90 mt-1.5 leading-relaxed">Share a simple link and loved ones can add their own memories of you — no account needed. You review each one and choose which join your story.</p>
        </div>
        <button data-testid="home-invite-family-btn" onClick={() => navigate("/family")}
          className="inline-flex items-center justify-center gap-2 bg-white text-coral hover:bg-cream px-6 h-12 rounded-full font-semibold whitespace-nowrap transition-colors">
          <Users size={18} /> Invite family &amp; friends
        </button>
      </div>

      {/* Sample memoirs and testimonials live on the public landing page, not the logged-in dashboard. */}
    </div>
  );
}
